import React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Activity } from '@/components/custom/ActivityMenu';

export interface SubNavActivitiesProps {
  activities: Activity[];
  isExpanded?: boolean;
  onToggle?: () => void;
  onViewAll?: () => void;
  variant?: 'card' | 'sheet';
}

export const SubNavActivities: React.FC<SubNavActivitiesProps> = ({
  activities,
  isExpanded = false,
  onToggle,
  onViewAll,
  variant = 'card'
}) => {
  if (!activities || activities.length === 0) return null;

  const sectionId = React.useId();

  const renderActivity = (activity: Activity, index: number) => {
    const Icon = activity.icon;
    return (
      <div key={index} className="px-4 py-2 hover:bg-gray-50 rounded transition-colors">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex-shrink-0">
            <Icon className="w-4 h-4 text-gray-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm">
              <span className="font-medium text-gray-900">{activity.action}</span>
              {activity.description && (
                <span className="text-gray-600"> {activity.description} </span>
              )}
              {activity.patient && (
                <button className="text-sky-600 hover:text-sky-700 font-medium">
                  {activity.patient}
                </button>
              )}
            </div>
          </div>
          <span className="text-xs text-gray-500 ml-2">{activity.timestamp}</span>
        </div>
      </div>
    );
  };

  if (variant === 'card') {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-base font-semibold text-gray-900">Recent Activities</h3>
          {onViewAll && (
            <button
              type="button"
              className="text-sm text-sky-600 hover:text-sky-700 font-medium"
              onClick={onViewAll}
            >
              View all
            </button>
          )}
        </div>
        <div className="space-y-2">
          {activities.slice(0, 5).map(renderActivity)}
        </div>
      </div>
    );
  }

  // Sheet variant with collapsible header
  return (
    <div className="border-b border-gray-100">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isExpanded}
        aria-controls={sectionId}
        className="w-full flex items-center justify-between py-3 px-2 text-left hover:bg-gray-50 rounded transition-colors"
      >
        <span className="text-sm font-medium text-gray-900">Recent Activities</span>
        <ChevronDown 
          className={cn(
            "w-4 h-4 text-gray-400 transition-transform",
            isExpanded && "rotate-180"
          )}
        />
      </button>
      {isExpanded && (
        <div className="pb-3" id={sectionId} role="region" aria-label="Recent Activities">
          {activities.map(renderActivity)}
          {onViewAll && (
            <button
              type="button"
              className="w-full text-center py-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
              onClick={onViewAll}
            >
              👁 View all
            </button>
          )}
        </div>
      )}
    </div>
  );
};

SubNavActivities.displayName = 'SubNavActivities';
